/**
 * Web Component for custom load more pagination functionality.
 *
 * This web component is responsible for loading in pages using the Shopify pages API. Its core functionalities include:
 *     - Handling page generation/removal via the API and window.history
 *     - Updating the URL to match the page being scrolled over for SEO and link sharing
 *     - Load Previous & Load More functionality
 */

// Dev Note: This functionality is ported from Furn at the client's request, with some modifications to suit this particular repo's set-up

class LoadMore extends HTMLElement {
  // IDs
  static gridContainer = "ProductGridContainer";
  static grid = "product-grid";
  static loadMoreButton = "LoadMoreButton";
  static loadMoreText = "LoadMoreText";
  static loadPrevButton = "LoadPreviousButton";

  // Classes
  static loadButtons = "pagination-load-button";
  static paginationWrapper = "filter-form__pagination-count";
  static paginationSection = "filter-form__pagination";

  constructor(button = null, previous) {
    super();

    this.debouncedOnClick = debounce((event) => {
      if (previous) this.onLoadPreviousRequested(event);
      else this.onLoadMoreRequested(event);
    }, 500);

    const loadMoreButton = this.querySelector(
      `#${button ? button : LoadMore.loadMoreButton}`,
    );

    // Keep button click for manual loading if needed
    if (loadMoreButton) {
      loadMoreButton.addEventListener(
        "click",
        this.debouncedOnClick.bind(this),
      );

      // Setup the initial and previous pointers using the starting page
      LoadMore.pageNumberInitial = parseInt(
        loadMoreButton.getAttribute("data-initial-page"),
      );
      LoadMore.pageNumberPrev = parseInt(
        loadMoreButton.getAttribute("data-initial-page"),
      );
      LoadMore.pageNumberMax = parseInt(
        loadMoreButton.getAttribute("data-max-page"),
      );

      LoadMore.pageList = [LoadMore.pageNumberInitial];

      // Attach the loading buttons to the class
      LoadMore.loadMore = document.getElementById(LoadMore.loadMoreButton);
      LoadMore.loadPrev = document.getElementById(LoadMore.loadPrevButton);

      LoadMore.scrollPositionResetAt = new Date().getTime();

      // Setup infinite scroll with Intersection Observer
      if (!previous) this.setupInfiniteScroll();
    }
  }

  /**
   * Sets up infinite scroll using Intersection Observer API.
   * Automatically loads more products when user scrolls near the bottom.
   */
  setupInfiniteScroll() {
    // Create a sentinel element to observe
    const sentinel = document.createElement("div");
    sentinel.className = "infinite-scroll-sentinel";
    sentinel.style.height = "1px";
    sentinel.style.position = "relative";

    // Insert sentinel before the load more button container
    const container = this.querySelector(".pagination-load-more-container");
    if (container) {
      container.parentNode.insertBefore(sentinel, container);
    }

    // Create Intersection Observer with a threshold to trigger before reaching the bottom
    const options = {
      root: null,
      rootMargin: "500px", // Trigger 500px before sentinel comes into view
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !LoadMore.isLoading) {
          // Check if there are more pages to load
          const currentPage = LoadMore.getHighestVisiblePage();
          if (currentPage < LoadMore.pageNumberMax)
            this.onLoadMoreRequested(new Event("scroll"));
        }
      });
    }, options);

    this.observer.observe(sentinel);
  }

  /**
   * Returns whether a browser is Webkit-based or not.
   *
   * @n     * @returns {boolean} Whether the browser is Webkit-based.
   */
  static isWebkitBrowser() {
    return (
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor)
    );
  }

  /**
   * Responsible for storing the target element on a collection page, so the
   * user's original position can be restored when navigating back in the browser history.
   */
  static setProductGridListeners() {
    var productGrid = document.getElementById(LoadMore.grid);
    if (productGrid && typeof productGrid !== "undefined") {
      let targetElement = null;

      // Stores the ID of the element to scroll to when loading a collections page.
      const storeScrollElement = (targetElement, clickTarget, swatchClick) => {
        // Ensure the user is clicking an element in the product index card
        if (clickTarget !== targetElement && clickTarget.tagName !== "BUTTON") {
          localStorage.setItem(
            "load-more-scroll-pos",
            JSON.stringify({
              id: targetElement.id,
              position: targetElement.getBoundingClientRect().top * -1,
              collectionUrl: window.location.href,
            }),
          );
          history.scrollRestoration = "manual";

          // Use setTimeout as it is non-blocking, so statements outside of it will
          // be completed first - ensuring this assignment is completed after localStorage.
          setTimeout(function () {
            if (!swatchClick) {
              window.location.href = targetElement.querySelector("a").href;
            } else {
              window.location.href = clickTarget.href;
            }
          });
        }
      };

      // Listen for clicks on the product grid and find which product was clicked.
      // If a product was selected, push its path to history and store the element.
      productGrid.addEventListener("click", function (event) {
        event.preventDefault();

        let swatchClick = false;

        if (event.target.classList.contains("collection-swatch")) {
          swatchClick = true;
        }

        targetElement = event.target.closest(".card-wrapper");
        if (targetElement && typeof targetElement !== "undefined")
          storeScrollElement(targetElement, event.target, swatchClick);
      });
    }
  }

  /**
   * Returns whether a browser is Webkit-based or not.
   *
   * The filters are applied via a fetch request, so any manual back-button presses need to
   * be intercepted and handled to simulate going back.
   */
  static setHistoryChangeListeners() {
    window.addEventListener("pageshow", (event) => {
      // Always try to restore the scroll position on a pageshow event
      const loadMoreScrollPos = localStorage.getItem("load-more-scroll-pos");

      if (loadMoreScrollPos && loadMoreScrollPos !== null) {
        const lastURL = JSON.parse(loadMoreScrollPos).collectionUrl;

        if (window.location.href === lastURL) LoadMore.restoreScrollPosition();
      }
    });

    window.addEventListener("popstate", (event) => {
      // Check when the position was last updated, continue if "popstate" is not called
      // back to back with "pageshow" in restoreScrollPosition
      let lastPositionResetTime = LoadMore.scrollPositionResetAt;
      if (Math.abs(lastPositionResetTime - new Date().getTime) > 3) {
        // Handle only the URL change logic
        if (
          event.state &&
          event.state.path &&
          typeof event.state.path !== "undefined"
        ) {
          const url = new URL(event.state.path);
          const urlParams = url.searchParams;
          let eventPage = parseInt(urlParams.get("page"));

          // Logic for determining which page to fetch
          let page;
          let remove;
          if (LoadMore.pageList.includes(eventPage)) {
            page = LoadMore.pageList.pop();
            remove = true;
          } else {
            page = eventPage;
            remove = false;
            LoadMore.pageList.push(page);
          }

          LoadMore.fetchNextPage(page, remove, false);
          LoadMore.pageNumberPrev = eventPage;
        }
      }
    });
  }

  /**
   * Renders the individual elements that make up the count and product grid.
   *
   * @param {*} html The HTML to load in.
   * @param {*} event The event that fired the render.
   */
  static renderData(html, remove = false) {
    let container = document.getElementById(LoadMore.gridContainer);
    let prevProductCount = container.querySelector(
      `.${LoadMore.paginationSection}`,
    );

    // Render the product grid
    LoadMore.renderProductGridContainer(html, remove);

    // If loading a previous page, render the product count
    if (LoadMore.pageNumberPrev <= LoadMore.getLowestVisiblePage())
      LoadMore.renderProductCount(
        html,
        remove && prevProductCount ? prevProductCount.outerHTML : null,
      );

    // Toggle the loading classes
    LoadMore.toggleLoading(true);

    if (LoadMore.loadPrev && typeof LoadMore.loadPrev !== "undefined") {
      // Hide the "Load More" button if the user has reached the first results page
      if (LoadMore.pageNumberPrev == 1 || LoadMore.pageNumberInitial == 1)
        LoadMore.loadPrev.style.display = "none";
      else LoadMore.loadPrev.style.display = "flex";
    }

    if (LoadMore.loadMore && typeof LoadMore.loadMore !== "undefined") {
      // Hide the "Load Previous" button if the user reached the last results page
      if (
        LoadMore.pageNumberPrev == LoadMore.pageNumberMax ||
        LoadMore.pageNumberInitial == LoadMore.pageNumberMax
      )
        LoadMore.loadMore.parentElement.style.display = "none";
      else LoadMore.loadMore.parentElement.style.display = "flex";
    }

    if (LoadMore.isPreviousLoad || remove) {
      var pageMarker = LoadMore.pageNumberPrev;
      if (remove)
        pageMarker =
          LoadMore.pageList.length > 0
            ? LoadMore.pageList.slice(-1)[0]
            : LoadMore.pageNumberInitial;

      var pageCountHeader = document.querySelector(
        `[data-page-marker="${pageMarker}"]`,
      );
      if (pageCountHeader && typeof pageCountHeader !== "undefined") {
        setTimeout(() => {
          pageCountHeader.scrollIntoView({
            block: "start",
            inline: "nearest",
          });
        }, 100);
      }
    }
  }

  /**
   * Renders the page/section from the existing cache.
   *
   * @param {*} filterDataUrl The URL for the cache to find.
   */
  static renderSectionFromCache(filterDataUrl, remove) {
    // Grab the HTML from the stored URL/HTML pairs in the cache.
    const html = LoadMore.loadData.find(filterDataUrl).html;

    LoadMore.renderData(html, remove);
  }

  /**
   * Renders the page/section from a fresh fetch of a URL.
   *
   * @param {*} url The URL to fetch.
   */
  static renderSectionFromFetch(url) {
  fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Load more request failed: ${response.status}`);
      }

      return response.text();
    })
    .then((responseText) => {
      const html = responseText;

      LoadMore.loadData = [
        ...LoadMore.loadData,
        { html, url }
      ];

      LoadMore.renderData(html);
    })
    .catch((error) => {
      console.error("Load more error:", error);
    })
    .finally(() => {
      LoadMore.toggleLoading(false);
    });
}

  /**
   * Restores the user's scroll position in the collections page
   * if they've navigated back from a PLP using browser back/forward buttons.
   *
   */
  static restoreScrollPosition() {
    let element = null;

    const scrollStateJSON = localStorage.getItem("load-more-scroll-pos");

    if (scrollStateJSON) {
      const scrollState = JSON.parse(scrollStateJSON);

      element = document.getElementById(scrollState.id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView();
          window.scrollBy(0, scrollState.position);
        }, 0);
      }
      localStorage.removeItem("load-more-scroll-pos");
    }

    LoadMore.resetScrollRestoration();

    return element;
  }

  /**
   * Restores the history.scrollRestoration value if the manual scroll has
   * been completed. The restoration takes place after 5 seconds to ensure
   * all browser-based scroll events have fired.
   */
  static resetScrollRestoration() {
    LoadMore.scrollPositionResetAt = new Date().getTime();

    if (
      history.scrollRestoration == "manual" &&
      !localStorage.getItem("load-more-scroll-pos")
    ) {
      setTimeout(function () {
        history.scrollRestoration = "auto";
      }, 5000);
    }
  }

  /**
   * Fetches the next page to be loaded in.
   *
   * @param {*} page The current page.
   * @param {*} updateHistory Whether to update the history or not.
   */
  static fetchNextPage(page, removeSection, updateHistory = true) {
    // console.log('Requested to load page', page, 'with remove set to', removeSection);

    // Disable fetch if the user is trying to load before page 1 or after the last page
    if (
      page <= 0 ||
      page > LoadMore.pageNumberMax ||
      (page == LoadMore.getLowestVisiblePage() &&
        page == LoadMore.getHighestVisiblePage())
    )
      return;

    LoadMore.isPreviousLoad = page < LoadMore.getLowestVisiblePage();

    LoadMore.pageNumberPrev = page;

    let productGrid = document.getElementById(LoadMore.grid);

    if (productGrid && typeof productGrid !== "undefined") {
      // Toggle the loading classes
      LoadMore.toggleLoading();

      // Grab the section ID and generate the URL to load from
      var sectionId = productGrid.dataset.id;

      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      urlParams.set("page", page);

      const allParamsString = urlParams.toString();

      const url = `${window.location.pathname}?section_id=${sectionId}&${allParamsString}`;
      const filterDataUrl = (element) => element.url === url;

      // Check whether filterDataUrl exists in the filterData array. If it does, the user has likely
      // gone back in the history, so we load from the cache. Otherwise, we load the URL from scratch.
      if (LoadMore.loadData.some(filterDataUrl)) {
  LoadMore.renderSectionFromCache(filterDataUrl, removeSection);
  LoadMore.toggleLoading(false);
} else {
  LoadMore.renderSectionFromFetch(url);
}

      if (updateHistory) LoadMore.pushToHistory(page);
    }
  }

  static resetPaginationState() {
  LoadMore.pageList = [1];
  LoadMore.pageNumberInitial = 1;
  LoadMore.pageNumberPrev = 1;
  LoadMore.isLoading = false;
  LoadMore.loadData = [];

  if (LoadMore.loadMore) {
    LoadMore.loadMore.style.display = "flex";
  }

  if (LoadMore.loadPrev) {
    LoadMore.loadPrev.style.display = "none";
  }
}

  /**
   * Pushes the current URL state to history.
   *
   * @param {*} page The current page.
   */
  static pushToHistory(page) {
    LoadMore.pageList.push(page);

    const historyUrl = new URL(window.location.href);
    const searchParams = new URLSearchParams(historyUrl.search);
    searchParams.set("page", page);

    const newUrl = `${historyUrl.origin}${historyUrl.pathname}?${searchParams.toString()}${historyUrl.hash}`;
    history.pushState({ path: newUrl }, "", newUrl);
  }

  /**
   * Returns the highest header element in the DOM.
   *
   * @returns The element
   */
  static getHighestHeaderElement() {
    let markers = document.querySelectorAll("[data-page-marker]");

    const elements = Array.from(markers);
    const highestElement = elements.reduce((highest, current) => {
      return LoadMore.getPageMarker(highest) > LoadMore.getPageMarker(current)
        ? highest
        : current;
    });

    return highestElement;
  }

  /**
   * Returns the highest currently visible page.
   *
   * @returns {int} The highest currently visible page number.
   */
  static getHighestVisiblePage() {
    var highestElement = LoadMore.getHighestHeaderElement();

    return LoadMore.getPageMarker(highestElement);
  }

  /**
   * Returns the lowest currently visible page.
   *
   * @returns {int} The lowest currently visible page number.
   */
  static getLowestVisiblePage() {
    let markers = document.querySelectorAll("[data-page-marker]");

    const elements = Array.from(markers);
    const lowestElement = elements.reduce((lowest, current) => {
      return LoadMore.getPageMarker(lowest) < LoadMore.getPageMarker(current)
        ? lowest
        : current;
    });

    return LoadMore.getPageMarker(lowestElement);
  }

  /**
   * Returns the product count for the current pages loaded.
   *
   * @returns {Object} Object containing the shown items and total.
   */
  static getProductCount() {
    var highestElement = LoadMore.getHighestHeaderElement();
    var paginationWrapper = highestElement.querySelector(
      `.${LoadMore.paginationWrapper}`,
    );

    console.log(highestElement);
    console.log(paginationWrapper);

    return {
      showing: paginationWrapper.getAttribute("data-items-count-current"),
      total: paginationWrapper.getAttribute("data-items-count-total"),
    };
  }

  /**
   * Returns the page marker value as an integer.
   *
   * @param {*} element The element to fetch the value from.
   * @returns {int} The page marker integer value.
   */
  static getPageMarker(element) {
    if (element.hasAttribute("data-page-marker"))
      return parseInt(element.getAttribute("data-page-marker"));

    return 0;
  }

  /**
   * Renders the product count container after the updated DOM is fetched.
   *
   * @param {*} html The HTML to be used to replace the existing count container.
   */
  static renderProductCount(html, parsedHTML = null) {
    // Grab the returned products count section HTML
    var parser = new DOMParser();

    if (!parsedHTML)
      parsedHTML = parser
        .parseFromString(html, "text/html")
        .querySelector(`.${LoadMore.paginationSection}`).outerHTML;

    document.querySelector(`.${LoadMore.paginationSection}`).outerHTML =
      parsedHTML;
  }

  /**
   * Renders the product grid container after the updated DOM is fetched.
   *
   * @param {*} html The HTML to be used to replace the existing grid container content.
   * @param {boolean} remove Whether or not the elements should be removed or appended.
   */
  static renderProductGridContainer(html, remove) {
    var productGrid = document.getElementById(LoadMore.grid);
    if (productGrid && typeof productGrid !== "undefined") {
      // Grab the returned products HTML
      var parser = new DOMParser();
      var parsedHTML = parser
        .parseFromString(html, "text/html")
        .getElementById(LoadMore.grid).innerHTML;
      var parsedResultsHTML = parser
        .parseFromString(html, "text/html")
        .querySelector(`.${LoadMore.paginationSection}`).outerHTML;

      let highestPage = LoadMore.getHighestVisiblePage();
      let lowestPage = LoadMore.getLowestVisiblePage();

      if (remove) {
        // Convert the parsedHTML string into an array of elements
        let tempDiv = document.createElement("div");
        tempDiv.innerHTML = parsedHTML;
        let elementsToRemove = Array.from(tempDiv.children);

        // Remove the lowest header in the product grid (the one relevant to the items being removed)
        var countHeaders = productGrid.querySelectorAll(
          `.${LoadMore.paginationSection}`,
        );

        if (LoadMore.pageNumberPrev == lowestPage) {
          // Remove the top header if loading previous
          countHeaders[0].remove();
        } else if (LoadMore.pageNumberPrev == highestPage) {
          // Remove the bottom header if loading more
          countHeaders[countHeaders.length - 1].remove();
        }

        // Loop through the elements to remove and remove them from the product grid if they exist
        elementsToRemove.forEach((elementToRemove) => {
          let existingElement = productGrid.querySelector(
            `#${elementToRemove.id}`,
          );

          if (existingElement) existingElement.remove();
        });
      } else {
        if (LoadMore.pageNumberPrev <= lowestPage) {
          var existingCountHeader = document.querySelector(
            `.${LoadMore.paginationSection}`,
          );
          // Append the original product count header above the existing cards and below the new ones
          productGrid.insertAdjacentElement(
            "afterbegin",
            existingCountHeader.cloneNode(true),
          );

          productGrid.insertAdjacentHTML("afterbegin", parsedHTML);
        } else if (LoadMore.pageNumberPrev > highestPage) {
          // Add a page-marker attribute to the page header title
          let tempDiv = document.createElement("div");
          tempDiv.innerHTML = parsedResultsHTML;
          tempDiv.children[0].setAttribute(
            "data-page-marker",
            LoadMore.pageNumberPrev,
          );

          const childNodes = tempDiv.childNodes;
          const serializedNodes = [];
          for (let i = 0; i < childNodes.length; i++)
            serializedNodes.push(childNodes[i].outerHTML);

          productGrid.insertAdjacentHTML("beforeend", serializedNodes.join(""));
          // Append the product cards to the product grid
          productGrid.insertAdjacentHTML("beforeend", parsedHTML);
        }
      }
    }
  }

  /**
   * Toggles the loading class that applies to the product grid and buttons
   * when aspects of the UI are updating during a fetch.
   */
  static toggleLoading(isLoading) {
  let productGrid = document.getElementById(LoadMore.grid);

  LoadMore.isLoading = isLoading;

  if (productGrid) {
    productGrid.classList.toggle("loading", isLoading);
  }

  let buttons = document.querySelectorAll(
    `.${LoadMore.loadButtons}`
  );

  buttons.forEach((button) => {
    var defaultButtonContent =
      button.getAttribute("data-default-text");

    if (!defaultButtonContent) return;

    if (isLoading) {
      button.innerHTML = `
        <div class="loading__spinner">
          <svg
            aria-hidden="true"
            focusable="false"
            class="spinner"
            viewBox="0 0 66 66"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              class="path"
              fill="none"
              stroke-width="6"
              cx="33"
              cy="33"
              r="30"
            ></circle>
          </svg>
        </div>
      `;
    } else {
      button.innerHTML = defaultButtonContent;

      var loadMoreText =
        document.getElementById(LoadMore.loadMoreText);

      if (loadMoreText) {
        var productCounts = LoadMore.getProductCount();

        if (productCounts) {
          loadMoreText.textContent =
            `showing ${productCounts.showing} of ${productCounts.total}`;
        }
      }
    }

    button.classList.toggle("loading", isLoading);
  });
}

  /**
   * Fires the fetchNextPage method to grab the next set of products. For example, if
   * the user is on page 4, this method will fetch page 5.
   *
   * @param {*} event The event that triggered the fetch
   */
  onLoadMoreRequested(event) {
    event.preventDefault();
    LoadMore.fetchNextPage(LoadMore.getHighestVisiblePage() + 1, false, true);
  }

  /**
   * Fires the fetchNextPage method to grab the previous set of products. For example, if
   * the user is on page 4, this method will fetch page 3.
   *
   * @param {*} event The event that triggered the fetch
   */
  onLoadPreviousRequested(event) {
    event.preventDefault();
    LoadMore.fetchNextPage(LoadMore.getLowestVisiblePage() - 1, false, true);
  }
}

customElements.define("load-more", LoadMore);

LoadMore.loadData = [];
LoadMore.isLoading = false;
LoadMore.setProductGridListeners();
LoadMore.setHistoryChangeListeners();

class LoadPrevious extends LoadMore {
  // IDs
  static loadPreviousButton = "LoadPreviousButton";

  constructor() {
    super(LoadPrevious.loadPreviousButton, true);
  }
}

customElements.define("load-previous", LoadPrevious);

LoadPrevious.loadData = [];

document.addEventListener("facets:updated", () => {
  LoadMore.resetPaginationState();
});